import { API_CONFIG, API_ENDPOINTS, getHeadersWithOrg, getMultipartHeaders, apiCall, authFetch } from './client';
import i18n from '../../i18n';
import {
  ErrorResponse,
  DocumentContentResponse,
  DocumentMetadataResponse,
  DocumentDeleteResponse,
  PublishedUrlMapping,
  PublishedUrlUpdateResponse,
  SearchResponse,
  FolderBrowseResponse,
  FolderCreateResponse,
  NodeResolveResponse,
  DeleteJobResponse,
  DeleteJobStatus,
  SingleFileIndexResponse,
} from '../../types';

/**
 * Fetch all metadata for a single document in one call (quality scores, feedback, attribution).
 */
export async function fetchDocumentMetadata(
  siteId: string,
  orgId: string,
  assetGuid: string
): Promise<DocumentMetadataResponse> {
  return apiCall<DocumentMetadataResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DOCUMENT_METADATA(siteId, assetGuid)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch the content of a single DITA document by its asset_guid
 */
export async function fetchDocumentContent(
  siteId: string,
  orgId: string,
  assetGuid: string
): Promise<DocumentContentResponse> {
  return apiCall<DocumentContentResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DOCUMENT_CONTENT(siteId, assetGuid)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Update a single DITA document by re-indexing it via the single-file index API.
 * Constructs a multipart form with the edited content as a file upload and the
 * asset_guid as the guid form field.
 *
 * The endpoint runs asynchronously (same contract as bulk-index) and returns
 * a job_id immediately -- the caller must poll GET /sites/{siteId}/index?job_id=...
 * (e.g. via useIndexingPolling) to learn the outcome.
 */
export async function updateDocumentContent(
  siteId: string,
  orgId: string,
  assetGuid: string,
  content: string,
  fileName: string,
): Promise<SingleFileIndexResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.INDEX(siteId)}`;

  const formData = new FormData();
  const blob = new Blob([content], { type: 'application/xml' });
  formData.append('file', blob, fileName);
  formData.append('guid', assetGuid);

  try {
    const response = await authFetch(url, {
      method: 'POST',
      headers: getMultipartHeaders(orgId),
      body: formData,
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to update document: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.documents.updateFailed'));
  }
}

/**
 * Delete one or more indexed DITA documents from a site.
 * Performs clean graph cleanup on the backend, preserving shared nodes.
 */
export async function deleteDocuments(
  siteId: string,
  orgId: string,
  assetGuids: string[],
): Promise<DocumentDeleteResponse> {
  return apiCall<DocumentDeleteResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DELETE_DOCUMENTS(siteId)}`,
    {
      method: 'DELETE',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ asset_guids: assetGuids }),
    },
  );
}

/**
 * Bulk-update published URLs on File nodes for a site.
 */
export async function updatePublishedUrls(
  siteId: string,
  orgId: string,
  mappings: PublishedUrlMapping[],
): Promise<PublishedUrlUpdateResponse> {
  return apiCall<PublishedUrlUpdateResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.PUBLISHED_URLS(siteId)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ mappings }),
    },
  );
}

/**
 * Batch-resolve asset GUIDs (image file paths) to their public URLs.
 * Returns a map of asset_guid -> url for assets that have been uploaded to S3.
 */
export async function resolveAssetUrls(
  siteId: string,
  assetGuids: string[],
): Promise<Record<string, string>> {
  const resp = await apiCall<{ urls: Record<string, string> }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.RESOLVE_ASSET_URLS(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ asset_guids: assetGuids }),
    },
  );
  return resp.urls;
}

/**
 * Perform a composite search (fused full-text + semantic) across indexed
 * documents for a site via the flameback-data service.
 */
export async function searchDocuments(
  siteId: string,
  orgId: string,
  query: string,
  topK: number = 20,
): Promise<SearchResponse> {
  return apiCall<SearchResponse>(
    `${API_CONFIG.DATA_BASE_URL}${API_ENDPOINTS.SEARCH(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({
        query,
        search_type: 'COMPOSITE',
        site_id: siteId,
        org_id: orgId,
        top_k: topK,
      }),
    },
  );
}

// ---- Folder hierarchy (content_nodes tree) ----

/** Browse a folder's immediate children ('0' = root). Optional in-location name filter. */
export async function browseFolder(
  siteId: string,
  parentFolderId: string = '0',
  opts: {
    q?: string;
    page?: number;
    pageSize?: number;
    sortBy?: 'name' | 'modified' | 'type';
    sortOrder?: 'asc' | 'desc';
    nodeTypes?: string[];
  } = {},
): Promise<FolderBrowseResponse> {
  const { q, page = 1, pageSize = 50, sortBy = 'name', sortOrder = 'asc', nodeTypes } = opts;
  let url =
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SITE_DOCUMENTS(siteId)}` +
    `?parent_folder_id=${encodeURIComponent(parentFolderId)}` +
    `&page=${page}&page_size=${pageSize}` +
    `&node_sort=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}`;
  if (q) url += `&q=${encodeURIComponent(q)}`;
  // Backend reuses the `document_types` query param for the folder-browse type filter.
  if (nodeTypes && nodeTypes.length) {
    for (const t of nodeTypes) url += `&document_types=${encodeURIComponent(t)}`;
  }
  return apiCall<FolderBrowseResponse>(url, { method: 'GET', headers: getHeadersWithOrg() });
}

/** Resolve a bare content_nodes id of unknown kind (folder or file), with the breadcrumb needed to render it. */
export async function resolveContentNode(
  siteId: string,
  id: string,
  signal?: AbortSignal,
): Promise<NodeResolveResponse> {
  return apiCall<NodeResolveResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SITE_NODE_RESOLVE(siteId, id)}`,
    { method: 'GET', headers: getHeadersWithOrg(), signal },
  );
}

/** Create an empty folder under parentFolderId ('0' = root). */
export async function createFolder(
  siteId: string,
  parentFolderId: string,
  name: string,
): Promise<FolderCreateResponse> {
  return apiCall<FolderCreateResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SITE_FOLDERS(siteId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ parent_folder_id: parentFolderId, name }),
    },
  );
}

/**
 * Thrown by {@link deleteNodes} when a delete job is already in progress
 * for this site. Carries the existing job's id so the caller can resume
 * polling it instead of hitting a dead end.
 */
export class DeleteConflictError extends Error {
  jobId: string | null;
  constructor(message: string, jobId: string | null) {
    super(message);
    this.name = 'DeleteConflictError';
    this.jobId = jobId;
  }
}

/** Delete files/folders (async). Returns a job id to poll. */
export async function deleteNodes(
  siteId: string,
  assetIds: string[],
): Promise<DeleteJobResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.SITE_NODES(siteId)}`;
  const response = await authFetch(url, {
    method: 'DELETE',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ asset_guids: assetIds }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = (body as { detail?: unknown } | null)?.detail;

    // A job-in-progress 409 carries a dict `detail` ({message, job_id}); any
    // other failure carries a plain string, same convention deleteSite uses
    // for its own 409 (detail: {message, orchestrators} vs. a plain string).
    if (
      response.status === 409 &&
      typeof detail === 'object' &&
      detail !== null &&
      typeof (detail as { job_id?: unknown }).job_id === 'string'
    ) {
      const { message, job_id: jobId } = detail as { message: string; job_id: string };
      throw new DeleteConflictError(message, jobId);
    }

    const errorData = body as ErrorResponse | null;
    const message =
      (typeof detail === 'string' ? detail : undefined) ||
      errorData?.error ||
      `Request failed: ${response.status}`;
    throw new Error(message);
  }

  return response.json();
}

/** Poll an async delete job. */
export async function pollDeleteJob(
  siteId: string,
  jobId: string,
): Promise<DeleteJobStatus> {
  return apiCall<DeleteJobStatus>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.DELETE_JOB(siteId, jobId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/**
 * Discover the site's currently active delete job, if any, without
 * already knowing its job_id -- mirrors `getIndexingStatus`'s no-jobId
 * discovery for indexing. Returns null when no job is active (404).
 */
export async function getActiveDeleteJob(siteId: string): Promise<DeleteJobStatus | null> {
  const response = await authFetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ACTIVE_DELETE_JOB(siteId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const body: ErrorResponse | null = await response.json().catch(() => null);
    throw new Error(body?.detail || body?.error || `Request failed: ${response.status}`);
  }
  return response.json();
}
